import { Button, Form, InputNumber, Modal, Space } from 'antd';
import Select, { DefaultOptionType } from 'antd/lib/select';
import _ from 'lodash';
import React, { ReactElement, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import IApplicationProperty from '~Api/Application/IApplicationProperty';
import PropertyValuationTypeEnum from '~Api/Application/PropertyValuationType';
import { applicationPropertyValuationValueAction } from '~Applications/actions';
import { propertyValuerValuationTypeLabels } from '~Applications/propertyValuerValuationTypeLabels';
import { IDictionary } from '~utilities/IDictionary';

interface IDefaultedValues {
    valuationType: PropertyValuationTypeEnum | null;
    valuationValue: number | null;
}

export default function UpdateValuationValueModal(props: {
    applicationProperty: IApplicationProperty;
    isOpen: boolean;
    onCancel: () => void;
}): ReactElement {
    const { applicationProperty, isOpen, onCancel } = props;

    const dispatch: Dispatch = useDispatch();

    const [ dirtyFields, setDirtyFields ] = useState<IDictionary<boolean>>({});
    const [ valuationType, setValuationType ] = useState<PropertyValuationTypeEnum | null>(null);
    const [ valuationValue, setValuationValue ] = useState<number | null>(null);

    const getDefaultedValues: () => IDefaultedValues = useCallback((): IDefaultedValues => ({
        valuationType: dirtyFields.valuationType ? valuationType : applicationProperty.valuationType,
        valuationValue: dirtyFields.valuationValue ? valuationValue : applicationProperty.valuationValue,
    }), [
        applicationProperty,
        dirtyFields,
        valuationType,
        valuationValue,
    ]);

    const onChangeValuationType: (value: PropertyValuationTypeEnum) => void = useCallback(
        (value: PropertyValuationTypeEnum): void => {
            setDirtyFields((previousDirtyFields: IDictionary<boolean>): IDictionary<boolean> => ({
                ...previousDirtyFields,
                valuationType: true,
            }));

            setValuationType(value);
        },
        [],
    );

    const onChangeValuationValue: (value: number) => void = useCallback((value: number): void => {
        setDirtyFields((previousDirtyFields: IDictionary<boolean>): IDictionary<boolean> => ({
            ...previousDirtyFields,
            valuationValue: true,
        }));

        setValuationValue(value);
    }, []);

    const onClickClear: () => void = useCallback((): void => {
        setDirtyFields((previousDirtyFields: IDictionary<boolean>): IDictionary<boolean> => ({
            ...previousDirtyFields,
            valuationValue: true,
        }));

        setValuationValue(null);
    }, []);

    const onClickOk: () => void = useCallback((): void => {
        const {
            valuationType: defaultedValuationType,
            valuationValue: defaultedValuationValue,
        } = getDefaultedValues();

        dispatch(applicationPropertyValuationValueAction(
            applicationProperty.uuid,
            defaultedValuationType,
            defaultedValuationValue,
        ));

        onCancel();
    }, [
        applicationProperty.uuid,
        dispatch,
        getDefaultedValues,
        onCancel,
    ]);

    const {
        valuationType: defaultedValuationType,
        valuationValue: defaultedValuationValue,
    } = getDefaultedValues();

    const valuationTypeOptions: DefaultOptionType[] = _.map(
        propertyValuerValuationTypeLabels,
        (label: string, value: string): DefaultOptionType => ({
            label,
            value,
        }),
    );

    return (
        <Modal
            okText='Update Valuation Value'
            onCancel={onCancel}
            onOk={onClickOk}
            open={isOpen}
            title='Update Valuation Value'
            wrapClassName='property-valuation-update-valuation-value-modal'
        >
            <Form.Item className='amount' label='Value'>
                <Space>
                    <InputNumber
                        addonBefore='$'
                        min={0}
                        onChange={onChangeValuationValue}
                        value={defaultedValuationValue}
                    />
                    <Button onClick={onClickClear}>Clear</Button>
                </Space>
            </Form.Item>
            <Form.Item className='valuation-type' label='Valuation Type'>
                <Select
                    onChange={onChangeValuationType}
                    options={valuationTypeOptions}
                    value={defaultedValuationType}
                />
            </Form.Item>
        </Modal>
    );
}
